import { useState, useEffect } from 'react';
// @mui
import {
  Box,
  Card,
  Stack,
  Switch,
  Typography,
  FormControlLabel,
  Chip,
  Grid,
  Button,
  CircularProgress,
  alpha,
  Theme,
} from '@mui/material';
import { useSnackbar } from '../../../../components/snackbar';
import { userApi } from 'src/api/user';
import { useUserContext } from 'src/auth/useUserContext';
import Iconify from '../../../../components/iconify';
import { translateKey } from 'src/locales/translate';


// ----------------------------------------------------------------------

// Define a type for the color property to avoid index signature errors
type ColorType =
  | 'error'
  | 'success'
  | 'warning'
  | 'info'
  | 'primary'
  | 'secondary';

const factTypeOptions = [
  {
    value: 'medical',
    label: 'Medyczne',
    color: 'error' as ColorType,
    icon: 'mdi:medical-bag',
    description:
      translateKey("hardcoded.fakty_zwiazane_ze_zdrowiem_chorobami_leczeniem_lub_opieka"),
  },
  {
    value: 'lifestyle',
    label: translateKey("hardcoded.styl_zycia"),
    color: 'success' as ColorType,
    icon: 'mdi:home-heart',
    description:
      translateKey("hardcoded.fakty_dotyczace_codziennej_opieki_karmienia_pielegnacji_lub_zycia"),
  },
  {
    value: 'behavioral',
    label: 'Behawioralne',
    color: 'warning' as ColorType,
    icon: 'mdi:paw',
    description: translateKey("hardcoded.fakty_o_zachowaniu_zwierzat_treningu_lub_komunikacji"),
  },
];

export default function AccountFactPreferences() {
  const { enqueueSnackbar } = useSnackbar();
  const { data: user, refetch } = useUserContext();
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Type assertion to handle the missing property in the type definition
    const userFactPreferences = (user as any)?.factPreferences;
    if (userFactPreferences) {
      setSelectedTypes(userFactPreferences);
    } else {
      // Default to all types if not set
      setSelectedTypes(['medical', 'lifestyle', 'behavioral']);
    }
  }, [user]);

  const handleToggleType = (type: string) => {
    setSelectedTypes((prev) => {
      if (prev.includes(type)) {
        // Don't allow removing the last type
        if (prev.length === 1) {
          enqueueSnackbar(translateKey("hardcoded.musisz_wybrac_przynajmniej_jeden_typ_faktow"), {
            variant: 'warning',
          });
          return prev;
        }
        return prev.filter((t) => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const handleSavePreferences = async () => {
    try {
      setLoading(true);
      await userApi.userControllerUpdateFactPreferences({
        updateFactPreferencesDto: { preferences: selectedTypes },
      });
      enqueueSnackbar(translateKey("hardcoded.preferencje_faktow_zostaly_zaktualizowane"), {
        variant: 'success',
      });
      refetch(); // Refresh user data
    } catch (error) {
      console.error('Error updating fact preferences:', error);
      enqueueSnackbar(translateKey("hardcoded.nie_udalo_sie_zaktualizowac_preferencji_faktow"), {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 3 }}>{translateKey("hardcoded.preferencje_tresci_o_zwierzetach")}</Typography>

      <Typography variant="body2" sx={{ mb: 3, color: 'text.secondary' }}>{translateKey("hardcoded.wybierz_typy_tresci_o_zwierzetach_ktore_chcesz_otrzymywac")}</Typography>

      <Grid container spacing={3} sx={{ mb: 3 }}>
        {factTypeOptions.map((type) => {
          const isSelected = selectedTypes.includes(type.value);
          return (
            <Grid item xs={12} md={6} key={type.value}>
              <Card
                sx={{
                  p: 2,
                  cursor: 'pointer',
                  paddingRight: 0,
                }}
                onClick={() => handleToggleType(type.value)}
              >
                <Stack direction="row" alignItems="flex-start" spacing={2}>
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      padding: 1,
                      borderRadius: 20,
                      bgcolor: (theme: Theme) =>
                        alpha(theme.palette[type.color].main, 0.12),
                      color: (theme: Theme) => theme.palette[type.color].main,
                    }}
                  >
                    <Iconify icon={type.icon} width={24} />
                  </Box>
                  <Box sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle2">{type.label}</Typography>
                    <Typography
                      variant="body2"
                      sx={{ color: 'text.secondary' }}
                    >
                      {type.description}
                    </Typography>
                  </Box>
                  <Switch
                    checked={isSelected}
                    onChange={(e) => {
                      e.stopPropagation(); // Prevent event from bubbling up to the card
                      handleToggleType(type.value);
                    }}
                    onClick={(e) => e.stopPropagation()} // Prevent click from reaching the card
                    sx={{
                      marginRight: 0,
                    }}
                  />
                </Stack>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          onClick={handleSavePreferences}
          disabled={loading}
          startIcon={
            loading ? <CircularProgress size={20} color="inherit" /> : null
          }
        >
          Zapisz preferencje
        </Button>
      </Box>
    </Card>
  );
}
